import type {
  PageWithRowIndices,
  PropertyComparisonPageData,
  PropertyFieldWithOptionalSplitting,
} from "@survey-types/survey.comparison.types";
import type { PropertyFieldRule } from "@survey-types/survey.core.types";
import type { MediaFiles } from "@survey-types/survey.media.types";
import type {
  Property,
  PropertyCustomFieldValue,
} from "@survey-types/survey.property.types";

/**
 * Pagination Constants
 *
 * Height calculations for different page orientations and positions
 */
export const PAGINATION_CONSTANTS = {
  // Height limits (in pixels)
  MAX_HEIGHT_LANDSCAPE_FIRST: 750, // First page with full header
  MAX_HEIGHT_LANDSCAPE_SUBSEQUENT: 780, // Subsequent pages with reduced header (65px saved)
  MAX_HEIGHT_PORTRAIT_FIRST: 1000, // First page with full header
  MAX_HEIGHT_PORTRAIT_SUBSEQUENT: 1000, // Subsequent pages with reduced header (65px saved)

  // Field limits per page
  MAX_FIELDS_LANDSCAPE: 60,
  MAX_FIELDS_PORTRAIT: 62,

  // Height per row estimates (in pixels)
  IMAGE_ROW_HEIGHT: 155,
  IMAGE_ROW_HEIGHT_LANDSCAPE: 120, // Estimated height for property images row in landscape mode
  DETAIL_ROW_HEIGHT: 24, // Base height per property detail row

  // Buffer space for content overflow
  HEIGHT_BUFFER: 50,

  // Content splitting constants
  LINE_HEIGHT: 15, // Height per line of text content in pixels
  LANDSCAPE_CHARS_PER_LINE: 23, // Characters per line in landscape mode
  PORTRAIT_CHARS_PER_LINE: 28, // Characters per line in portrait mode
  HEIGHT_THRESHOLD: 60, // Height threshold to trigger content splitting
} as const;

/**
 * Get maximum height available for content based on page position and orientation
 * @param hasHeader - Whether this page will have a PDF header (first page or first page of property set)
 * @param mode - Page orientation (Landscape or Portrait)
 */
export const getMaxHeight = (
  hasHeader: boolean,
  mode: "Landscape" | "Portrait",
): number => {
  if (mode === "Landscape") {
    return hasHeader
      ? PAGINATION_CONSTANTS.MAX_HEIGHT_LANDSCAPE_FIRST
      : PAGINATION_CONSTANTS.MAX_HEIGHT_LANDSCAPE_SUBSEQUENT;
  }
  return hasHeader
    ? PAGINATION_CONSTANTS.MAX_HEIGHT_PORTRAIT_FIRST
    : PAGINATION_CONSTANTS.MAX_HEIGHT_PORTRAIT_SUBSEQUENT;
};

/**
 * Get maximum fields per page based on orientation
 */
export const getMaxFieldsPerPage = (mode: "Landscape" | "Portrait"): number => {
  return mode === "Landscape"
    ? PAGINATION_CONSTANTS.MAX_FIELDS_LANDSCAPE
    : PAGINATION_CONSTANTS.MAX_FIELDS_PORTRAIT;
};

/**
 * Split properties into chunks based on maximum properties per page
 */
export const chunkProperties = (
  properties: Property[],
  maxProperties: number,
): Property[][] => {
  if (properties.length <= maxProperties) {
    return [properties];
  }

  const chunks: Property[][] = [];
  for (let i = 0; i < properties.length; i += maxProperties) {
    chunks.push(properties.slice(i, i + maxProperties));
  }
  return chunks;
};

/**
 * Content splitting utilities for handling large attribute values
 */
export const ContentSplitterUtils = {
  /**
   * Get max characters per line based on orientation
   */
  getMaxCharsPerLine(mode: "Landscape" | "Portrait"): number {
    return mode === "Landscape"
      ? PAGINATION_CONSTANTS.LANDSCAPE_CHARS_PER_LINE
      : PAGINATION_CONSTANTS.PORTRAIT_CHARS_PER_LINE;
  },

  /**
   * Calculate height needed for a text string
   */
  calculateTextHeight(text: string, maxCharsPerLine: number): number {
    if (!text) return PAGINATION_CONSTANTS.LINE_HEIGHT;

    const lines = this.splitTextIntoLines(text, maxCharsPerLine);
    return Math.round(lines.length * PAGINATION_CONSTANTS.LINE_HEIGHT);
  },

  /**
   * Check if a string is a URL or contains HTML tags with URLs
   * Similar to renderUrlOrValue's detection logic
   */
  isUrlOrHtmlPattern(text: string): boolean {
    // Check if it's a plain URL
    if (/^https?:\/\//i.test(text)) {
      return true;
    }

    // Check if it contains HTML tags
    if (/<[^>]+>/i.test(text)) {
      return true;
    }

    // Check if it contains URLs anywhere in the text (including within attributes)
    if (/(https?:\/\/[^\s<>"']+)/i.test(text)) {
      return true;
    }

    return false;
  },

  /**
   * Split text into lines that respect word boundaries
   * Special handling for URLs, HTML content, and long continuous text without spaces
   */
  splitTextIntoLines(text: string, maxCharsPerLine: number): string[] {
    if (!text) {
      return [""];
    }
    // Check if text contains URLs or HTML tags first, before length check
    // Apply minimal reduction for URL/HTML content to prevent overly aggressive splitting
    const isUrlOrHtml = this.isUrlOrHtmlPattern(text);
    const effectiveCharsPerLine = isUrlOrHtml
      ? maxCharsPerLine - 2
      : maxCharsPerLine;
    // If text fits within the effective character limit, return as single line
    if (text.length <= effectiveCharsPerLine) {
      return [text];
    }

    // If text contains URLs or HTML tags, treat entire text as single unit for splitting
    // This ensures alignment with how renderUrlOrValue processes the content
    if (isUrlOrHtml) {
      const lines: string[] = [];
      for (let i = 0; i < text.length; i += effectiveCharsPerLine) {
        lines.push(text.substring(i, i + effectiveCharsPerLine));
      }
      return lines;
    }

    // Check if text has no spaces (continuous text) - split by character count
    const hasNoSpaces = !text.includes(" ") && !text.includes("\n");
    if (hasNoSpaces && text.length > maxCharsPerLine) {
      const lines: string[] = [];
      for (let i = 0; i < text.length; i += maxCharsPerLine) {
        lines.push(text.substring(i, i + maxCharsPerLine));
      }
      return lines;
    }

    // Split by line breaks first to preserve them
    const paragraphs = text.split("\n");
    const lines: string[] = [];

    for (let pIndex = 0; pIndex < paragraphs.length; pIndex++) {
      const paragraph = paragraphs[pIndex];
      const isLastParagraph = pIndex === paragraphs.length - 1;
      const words = paragraph.split(" ");
      let currentLine = "";

      for (const word of words) {
        // If the word itself is longer than the limit, split it
        if (word.length > maxCharsPerLine) {
          // Add current line if it has content
          if (currentLine) {
            lines.push(currentLine);
            currentLine = "";
          }

          // Split the long word into chunks
          for (let i = 0; i < word.length; i += maxCharsPerLine) {
            lines.push(word.substring(i, i + maxCharsPerLine));
          }
          continue;
        }

        // Check if adding this word would exceed the limit
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (testLine.length > maxCharsPerLine) {
          // Push current line and start new one
          if (currentLine) {
            lines.push(currentLine);
          }
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }

      // Add the last line of this paragraph with \n to mark paragraph boundary
      if (currentLine) {
        lines.push(currentLine + "\n");
      } else if (!isLastParagraph) {
        // Empty paragraph - still need to preserve the line break
        lines.push("\n");
      }
    }

    return lines.length > 0 ? lines : [""];
  },

  /**
   * Split content based on available height in the page
   */
  splitContentByAvailableHeight(
    text: string,
    maxCharsPerLine: number,
    availableHeight: number,
  ): {
    currentPageLines: string[];
    remainingText: string;
    isContinued: boolean;
  } {
    if (!text) {
      return { currentPageLines: [""], remainingText: "", isContinued: false };
    }
    const allLines = this.splitTextIntoLines(text, maxCharsPerLine);
    const maxLinesForHeight = Math.floor(
      availableHeight / PAGINATION_CONSTANTS.LINE_HEIGHT,
    );

    if (allLines.length <= maxLinesForHeight) {
      return {
        currentPageLines: allLines,
        remainingText: "",
        isContinued: false,
      };
    }

    const currentPageLines = allLines
      .slice(0, maxLinesForHeight)
      .map((line) => line + " ");
    const remainingLines = allLines
      .slice(maxLinesForHeight)
      .map((line) => line + " ");

    // Join remaining lines while preserving paragraph boundaries marked with \n
    let remainingText = "";
    if (this.isUrlOrHtmlPattern(text)) {
      // For URLs/HTML, join with spaces since they were split at character boundaries
      remainingText = remainingLines.join(" ");
    } else {
      // For regular text, respect embedded \n markers for paragraph boundaries
      for (let i = 0; i < remainingLines.length; i++) {
        const line = remainingLines[i];
        if (i === 0) {
          remainingText = line;
        } else {
          // If previous line ended with \n, it's a paragraph boundary - don't add space
          // Otherwise it's word-wrapped text - add space
          const prevLineEndsWithNewline = remainingLines[i - 1].endsWith("\n");
          remainingText += prevLineEndsWithNewline ? line : " " + line;
        }
      }
    }

    return {
      currentPageLines,
      remainingText,
      isContinued: true,
    };
  },
};

/**
 * Check if field needs content splitting and process accordingly
 */
export const processFieldForContentSplitting = (
  field: PropertyFieldRule,
  properties: Property[],
  mode: "Landscape" | "Portrait",
  availableHeight: number,
  pendingContent?: Map<string, string>, // propertyId -> remaining text
): {
  processedField: PropertyFieldRule;
  splitContentMap?: Map<string, string[]>;
  remainingContentMap?: Map<string, string>;
  actualHeight: number;
  isContinued: boolean;
} => {
  const maxCharsPerLine = ContentSplitterUtils.getMaxCharsPerLine(mode);

  // Check if we have pending content from previous page
  if (pendingContent && pendingContent.size > 0) {
    const splitContentMap = new Map<string, string[]>();
    const remainingContentMap = new Map<string, string>();
    let maxLines = 1;
    let hasRemainingContent = false;

    // Only process properties that actually have pending content
    properties.forEach((property) => {
      const remainingText = pendingContent.get(property.propertyId);
      if (remainingText) {
        const { currentPageLines, remainingText: leftover } =
          ContentSplitterUtils.splitContentByAvailableHeight(
            remainingText,
            maxCharsPerLine,
            availableHeight,
          );

        // Only add to split content map if there are lines to show
        if (currentPageLines.length > 0) {
          splitContentMap.set(property.propertyId, currentPageLines);
          maxLines = Math.max(maxLines, currentPageLines.length);
        }

        if (leftover) {
          remainingContentMap.set(property.propertyId, leftover);
          hasRemainingContent = true;
        }
      }
      // Note: Properties without pending content will show empty/dash in PropertyDetailRow
    });

    return {
      processedField: field,
      splitContentMap: splitContentMap.size > 0 ? splitContentMap : undefined,
      remainingContentMap: hasRemainingContent
        ? remainingContentMap
        : undefined,
      actualHeight: maxLines * PAGINATION_CONSTANTS.LINE_HEIGHT,
      isContinued: true,
    };
  }

  // Calculate the maximum height this field would need
  // For normal text (not URLs/HTML), split into lines first if text is long enough
  let maxFieldHeight: number = PAGINATION_CONSTANTS.DETAIL_ROW_HEIGHT;
  let needsSplitting = false;
  // Track whether a URL/HTML value exceeds available height but fits on a full page.
  // In that case we prefer to move the entire field to the next page rather than
  // splitting mid-URL.
  let hasUrlThatFitsOnFullPage = false;

  properties.forEach((property) => {
    // Try to get value using fieldKey, fallback to fieldId
    const key = field.fieldKey;
    let value = key ? property[key as keyof Property] : null;
    // If still not found, check customFields array
    if (
      !value &&
      property.customFields &&
      Array.isArray(property.customFields)
    ) {
      const customField = (
        property.customFields as PropertyCustomFieldValue[]
      ).find((cf) => cf.customField?.name === field.fieldLabel);
      if (customField) {
        value = customField.value;
      }
    }
    if (value) {
      const stringValue = String(value);
      const isUrlOrHtml = ContentSplitterUtils.isUrlOrHtmlPattern(stringValue);

      // Calculate lines and height for both URL/HTML and plain text
      const lines = ContentSplitterUtils.splitTextIntoLines(
        stringValue,
        maxCharsPerLine - 2, // Slightly reduce to account for padding/margin
      );

      if (lines.length > 1) {
        const textHeight = lines.length * PAGINATION_CONSTANTS.LINE_HEIGHT;
        maxFieldHeight = Math.max(maxFieldHeight, textHeight);

        // For URL/HTML content that exceeds available height, check whether it
        // would fit on a single fresh page. If yes, prefer moving it whole to
        // the next page instead of splitting it mid-URL.
        if (isUrlOrHtml && textHeight > availableHeight) {
          const maxSinglePageHeight =
            getMaxHeight(false, mode) - PAGINATION_CONSTANTS.HEIGHT_BUFFER;
          if (textHeight <= maxSinglePageHeight) {
            hasUrlThatFitsOnFullPage = true;
          }
        }
      } else {
        maxFieldHeight = Math.max(
          maxFieldHeight,
          PAGINATION_CONSTANTS.DETAIL_ROW_HEIGHT,
        );
      }
    }
  });

  // Split across pages if the content height exceeds available height.
  // Exception: if a URL/HTML value exceeds available height but fits on a single
  // full page, skip splitting and let the pagination loop push it to the next page.
  if (maxFieldHeight > availableHeight && !hasUrlThatFitsOnFullPage) {
    needsSplitting = true;
  }

  if (!needsSplitting) {
    return {
      processedField: field,
      actualHeight: maxFieldHeight,
      isContinued: false,
    };
  }

  // Process content splitting
  const splitContentMap = new Map<string, string[]>();
  const remainingContentMap = new Map<string, string>();
  let maxLines = 1;
  let hasRemainingContent = false;

  properties.forEach((property) => {
    // Try to get value using fieldKey, fallback to fieldId
    const key = field.fieldKey;
    let value = key ? property[key as keyof Property] : null;
    // If still not found, check customFields array
    if (
      !value &&
      property.customFields &&
      Array.isArray(property.customFields)
    ) {
      const customField = (
        property.customFields as PropertyCustomFieldValue[]
      ).find((cf) => cf.customField?.name === field.fieldLabel);
      if (customField) {
        value = customField.value;
      }
    }
    if (value) {
      const { currentPageLines, remainingText } =
        ContentSplitterUtils.splitContentByAvailableHeight(
          String(value),
          maxCharsPerLine,
          availableHeight,
        );

      splitContentMap.set(property.propertyId, currentPageLines);
      maxLines = Math.max(maxLines, currentPageLines.length);

      if (remainingText) {
        remainingContentMap.set(property.propertyId, remainingText);
        hasRemainingContent = true;
      }
    }
  });

  return {
    processedField: field,
    splitContentMap,
    remainingContentMap: hasRemainingContent ? remainingContentMap : undefined,
    actualHeight: maxLines * PAGINATION_CONSTANTS.LINE_HEIGHT,
    isContinued: false,
  };
};

/**
 * Calculate paginated data for property comparison with intelligent content splitting
 *
 * This function implements the intelligent pagination algorithm:
 * 1. First splits properties horizontally (max 7 landscape / 4 portrait per page)
 * 2. Then splits fields vertically based on height constraints
 * 3. For fields with content height > 50px, splits content when it exceeds remaining page space
 * 4. Images row (135px) appears only on first page of each property set
 * 5. Uses 12px line height and character limits for accurate content splitting
 */
export const calculatePaginatedData = (
  properties: Property[],
  visibleFields: PropertyFieldRule[],
  maxProperties: number,
  mode: "Landscape" | "Portrait",
  isIncludeFlyersPC?: boolean,
  isIncludeSitePlansPC?: boolean,
  mediaFiles?: MediaFiles,
): PropertyComparisonPageData[] => {
  const pages: PropertyComparisonPageData[] = [];
  const propertyChunks = chunkProperties(properties, maxProperties);
  // Track pending content for fields that were split
  const pendingContentByField = new Map<string, Map<string, string>>(); // fieldId -> propertyId -> remaining text

  // For each property chunk, determine field pagination with content splitting
  propertyChunks.forEach((pageProperties) => {
    let remainingFields = [...visibleFields];
    let isFirstPageOfPropertySet = true;
    let hasCreatedAtLeastOnePage = false;

    while (
      remainingFields.length > 0 ||
      pendingContentByField.size > 0 ||
      !hasCreatedAtLeastOnePage
    ) {
      const currentPageFields: PropertyFieldWithOptionalSplitting[] = []; // Will contain both regular and extended field data
      const processedFieldIds = new Set<string>(); // Track which fields we've processed in this iteration
      let currentHeight = 0;

      // Get appropriate max height - account for header on first page of property set
      const hasHeader = isFirstPageOfPropertySet;
      const maxHeight = getMaxHeight(hasHeader, mode);
      // Include images on first page of each property set OR when showing header
      if (isFirstPageOfPropertySet || hasHeader) {
        currentHeight +=
          mode === "Landscape"
            ? PAGINATION_CONSTANTS.IMAGE_ROW_HEIGHT_LANDSCAPE
            : PAGINATION_CONSTANTS.IMAGE_ROW_HEIGHT;
        if (isIncludeFlyersPC) {
          let highestFlyerCount = 0;
          pageProperties.forEach((property) => {
            const propertyMedia = mediaFiles?.[property.propertyId];
            const flyer =
              propertyMedia?.fliers?.filter(
                (f) => !f.isHidden && f.cloudfrontUrl,
              ) ?? [];
            if (flyer.length > highestFlyerCount) {
              highestFlyerCount = flyer.length;
            }
          });

          currentHeight += 20 + highestFlyerCount * 15; // Add extra space if flyers are included
        }
        if (isIncludeSitePlansPC) {
          let highestSitePlanCount = 0;
          pageProperties.forEach((property) => {
            const propertyMedia = mediaFiles?.[property.propertyId];
            const sitePlan =
              propertyMedia?.floorPlans?.filter(
                (s) => !s.isHidden && s.cloudfrontUrl,
              ) ?? [];
            if (sitePlan.length > highestSitePlanCount) {
              highestSitePlanCount = sitePlan.length;
            }
          });

          currentHeight += 20 + highestSitePlanCount * 15; // Add extra space if site plans are included
        }
      }

      // First, process any pending content from previous pages
      const fieldsWithPendingContent: PropertyFieldRule[] = [];
      for (const [fieldId] of pendingContentByField.entries()) {
        const originalField = visibleFields.find((f) => f.fieldId === fieldId);
        if (originalField) {
          fieldsWithPendingContent.push(originalField);
        }
      }

      // Process fields with pending content first
      for (const field of fieldsWithPendingContent) {
        const pendingContent = pendingContentByField.get(field.fieldId);
        if (!pendingContent) continue;

        const availableHeight =
          maxHeight - currentHeight - PAGINATION_CONSTANTS.HEIGHT_BUFFER;

        if (availableHeight < PAGINATION_CONSTANTS.LINE_HEIGHT) break; // Not enough space for even one line

        const result = processFieldForContentSplitting(
          field,
          pageProperties,
          mode,
          availableHeight,
          pendingContent,
        );

        // Check if the processed field fits
        if (
          currentHeight + result.actualHeight >
          maxHeight - PAGINATION_CONSTANTS.HEIGHT_BUFFER
        ) {
          break;
        }

        // Create extended field data for content splitting
        const extendedField = {
          ...result.processedField,
          splitContentMap: result.splitContentMap,
          isContinued: result.isContinued,
        };

        currentPageFields.push(extendedField);
        currentHeight += result.actualHeight;
        processedFieldIds.add(field.fieldId); // Mark as processed

        // Update or remove pending content
        if (result.remainingContentMap && result.remainingContentMap.size > 0) {
          pendingContentByField.set(field.fieldId, result.remainingContentMap);
        } else {
          pendingContentByField.delete(field.fieldId);
        }
      }

      // Then process remaining fields
      let fieldIndex = 0;
      while (fieldIndex < remainingFields.length) {
        const field = remainingFields[fieldIndex];
        // Skip if this field already has pending content (already processed above)
        if (processedFieldIds.has(field.fieldId)) {
          fieldIndex++;
          continue;
        }

        const availableHeight =
          maxHeight - currentHeight - PAGINATION_CONSTANTS.HEIGHT_BUFFER;
        if (availableHeight < PAGINATION_CONSTANTS.LINE_HEIGHT) break; // Not enough space for even one line
        const result = processFieldForContentSplitting(
          field,
          pageProperties,
          mode,
          availableHeight,
        );

        // Check if the processed field fits
        if (
          currentHeight + result.actualHeight >
          maxHeight - PAGINATION_CONSTANTS.HEIGHT_BUFFER
        ) {
          break;
        }

        // Create field data (regular if no splitting, extended if splitting occurred)
        if (result.splitContentMap) {
          const extendedField = {
            ...result.processedField,
            splitContentMap: result.splitContentMap,
            isContinued: result.isContinued,
          };
          currentPageFields.push(extendedField);
        } else {
          currentPageFields.push(result.processedField);
        }

        currentHeight += result.actualHeight;
        processedFieldIds.add(field.fieldId); // Mark as processed

        // If there's remaining content, add it to pending content
        if (result.remainingContentMap && result.remainingContentMap.size > 0) {
          pendingContentByField.set(field.fieldId, result.remainingContentMap);
        }

        fieldIndex++;
      }

      // Create page data if we have any fields OR if this is the first page (to show PropertyCards)
      if (currentPageFields.length > 0 || isFirstPageOfPropertySet) {
        pages.push({
          properties: pageProperties,
          fields: currentPageFields,
          hasImages: isFirstPageOfPropertySet,
          isFirstPageOfPropertySet, // Add flag for showing header
        });
        hasCreatedAtLeastOnePage = true;
      }

      // Remove processed fields that have no more pending content
      remainingFields = remainingFields.filter(
        (field) =>
          !processedFieldIds.has(field.fieldId) ||
          pendingContentByField.has(field.fieldId),
      );
      isFirstPageOfPropertySet = false;

      // Safety check to prevent infinite loops
      if (currentPageFields.length === 0 && pendingContentByField.size === 0) {
        break;
      }
    }
  });

  return pages;
};

/**
 * Calculate pages with continuous row indices for proper styling
 *
 * This ensures alternating row backgrounds work correctly across page boundaries
 */
export const calculatePagesWithRowIndices = (
  paginatedData: PropertyComparisonPageData[],
): PageWithRowIndices[] => {
  let cumulativeRowIndex = 0;

  return paginatedData.map((pageData, pageIndex) => {
    const pageWithRowIndex: PageWithRowIndices = {
      pageData,
      pageIndex,
      startingRowIndex: cumulativeRowIndex,
    };

    // Update cumulative index for next page
    cumulativeRowIndex += pageData.fields.length;

    return pageWithRowIndex;
  });
};
